const isLocalHost =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1');

/**
 * Default local dev to deployed API so localhost UI still works without running
 * a separate backend. Override with REACT_APP_SERVER when you intentionally run
 * a local API (e.g. http://localhost:4000).
 */
const defaultServer = isLocalHost
  ? 'https://apis.starfarm.com'
  : 'https://api.starfarm.com';

const defaultStreamer = isLocalHost
  ? 'http://localhost:3001'
  : 'https://app.starfarm.com';

const defaultAccount = isLocalHost
  ? 'http://localhost:3000'
  : 'https://artist.starfarm.com';

const defaultGraphqlWs = isLocalHost
  ? 'ws://localhost:4000/graphql'
  : 'wss://api.starfarm.com/graphql';

/**
 * In deployed environments, prefer same-origin `/graphql` and let Vercel proxy
 * to the upstream API. This avoids browser CORS failures from artist/app origins.
 */
function resolveGraphqlEndpoint(raw: string): string {
  if (typeof window === 'undefined') return raw;
  const host = window.location.hostname;
  const isLocal = host === 'localhost' || host === '127.0.0.1';
  if (isLocal) return raw;

  // Prefer direct API on canonical web hosts to avoid occasional HTML fallthrough
  // from platform rewrites (which appears as "Unexpected token '<'" in Apollo).
  if (host.endsWith('.starfarm.com')) {
    return 'https://api.starfarm.com/graphql';
  }

  // Keep explicit relative endpoints.
  if (raw.startsWith('/')) return raw;

  // Force deployed clients to same-origin proxy endpoint.
  return '/graphql';
}

/**
 * Artists must not use the consumer app origin (`app.*`) when env points both
 * streamer + "account" at the same host (common Vercel mis-set). Prefer explicit
 * `REACT_APP_ARTIST_PORTAL_URL`; else if accountUri === streamerUri, use the
 * canonical artist subdomain (skip for localhost, where ports usually differ).
 */
function resolveArtistPortalUri(
  accountUri: string,
  streamerUri: string,
  explicit: string | undefined
): string {
  if (explicit) {
    return forceDeployedApiUrl(explicit);
  }
  try {
    const account = new URL(accountUri);
    const streamer = new URL(streamerUri);
    if (account.origin !== streamer.origin) {
      return accountUri;
    }
    if (account.hostname === 'localhost' || account.hostname === '127.0.0.1') {
      return accountUri;
    }
    return 'https://artist.starfarm.com';
  } catch {
    return accountUri;
  }
}

/**
 * CRA bakes `REACT_APP_*` at build time. If Vercel/CI accidentally has
 * `REACT_APP_GRAPHQL_ENDPOINT=http://localhost:4000/graphql`, production
 * will call the visitor's laptop instead of the API. Rewrite when the app is
 * not running on localhost.
 */
function forceDeployedApiUrl(url: string): string {
  if (typeof window === 'undefined') return url;
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return url;
  }
  if (!url.includes('localhost') && !url.includes('127.0.0.1')) {
    return url;
  }
  console.warn('[envs] Overriding localhost API URL for deployed host:', url);
  let u = url;
  u = u.replace(/^http:\/\/localhost:4000(\/.*)?$/, 'https://api.starfarm.com$1');
  u = u.replace(/^http:\/\/127\.0\.0\.1:4000(\/.*)?$/, 'https://api.starfarm.com$1');
  u = u.replace(/^ws:\/\/localhost:4000(\/.*)?$/, 'wss://api.starfarm.com$1');
  u = u.replace(/^ws:\/\/127\.0\.0\.1:4000(\/.*)?$/, 'wss://api.starfarm.com$1');
  u = u.replace(/^http:\/\/localhost:3001(\/.*)?$/, 'https://app.starfarm.com$1');
  u = u.replace(/^http:\/\/localhost:3000(\/.*)?$/, 'https://artist.starfarm.com$1');
  return u;
}

const streamerUri = forceDeployedApiUrl(process.env.REACT_APP_STREAMER_APP || defaultStreamer);
const accountUri = forceDeployedApiUrl(process.env.REACT_APP_ACCOUNT_APP || defaultAccount);
const artistPortalUri = resolveArtistPortalUri(accountUri, streamerUri, process.env.REACT_APP_ARTIST_PORTAL_URL);

const envs = {
  serverUri: forceDeployedApiUrl(process.env.REACT_APP_SERVER || defaultServer),
  streamerUri,
  accountUri,
  /** Base URL for artist-only flows (login redirect, subdomain fix). */
  artistPortalUri,
  adminDashboardUrl:
    process.env.REACT_APP_ADMIN_DASHBOARD_URL || 'https://admin.starfarm.com',
  apiBaseUrl: forceDeployedApiUrl(process.env.REACT_APP_API_BASE_URL || defaultServer),
  graphqlEndpoint: forceDeployedApiUrl(
    resolveGraphqlEndpoint(
      process.env.REACT_APP_GRAPHQL_ENDPOINT || `${defaultServer}/graphql`
    )
  ),
  graphqlWsEndpoint: forceDeployedApiUrl(
    process.env.REACT_APP_GRAPHQL_WS_ENDPOINT || defaultGraphqlWs
  ),
  cookiesDomain:
    process.env.REACT_APP_COOKIES_DOMAIN || (isLocalHost ? 'localhost' : '.starfarm.com'),
  environment:
    process.env.REACT_APP_ENVIRONMENT || (isLocalHost ? 'development' : 'production'),
  firebase: {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '',
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
    appId: process.env.REACT_APP_FIREBASE_APP_ID || '',
    vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY || '',
  },
  stripe: {
    publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '',
    clientId: process.env.REACT_APP_STRIPE_CLIENT_ID || '',
  }
};

export default envs;
